import { observer } from "mobx-react-lite";
import { useStore } from "../../stores/root-store-context";
import {
  Button,
  Form,
  FormProps,
  Input,
  Modal,
  Select,
  AutoComplete,
  Image,
  Flex,
  message,
  Spin,
} from "antd";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useCallback, useRef } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Roles } from "../../enums/Roles";

export type InviteUser = {
  email: string;
  firstName: string;
  lastName: string;
  personalEmails: string[];
  roles: (keyof typeof Roles)[];
  chiefId: string;
  jobTitleId: string;
};

export const AddNewUser = observer(
  ({ isModalOpen, hideModal }: { isModalOpen: boolean; hideModal: () => void }) => {
    const { employeesStore, applicationStore } = useStore();
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const [msg] = message.useMessage();
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      if (!employeesStore.jobTitles.length) {
        employeesStore.getJobTitles();
      }
    }, [employeesStore]);

    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    const onFinish: FormProps<any>["onFinish"] = async (values) => {
      const payload: InviteUser = {
        email: values.email.trim(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        personalEmails: values.personalEmails
          ? values.personalEmails.split(",").map((e: string) => e.trim()).filter(Boolean)
          : [],
        roles: values.roles,
        chiefId: values.chiefId,
        jobTitleId: values.jobTitleId,
      };

      setIsSubmitting(true);
      msg.open({ type: "loading", content: t("SendingInvitation"), key: "invitation" });

      employeesStore
        .inviteUser(payload)
        .then(async () => {
          msg.destroy("invitation");
          msg.success(t("TheInvitationWasSentToMail"));
          form.resetFields();
          hideModal();
          await employeesStore.getUsers();
        })
        .catch((err: any) => {
          msg.destroy("invitation");
          msg.error(err.message ?? "Invite failed");
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    };

    const validateEmail = async (email: string) => {
      setIsCheckingEmail(true);
      
      try {
        const res = await applicationStore.checkEmail(email);

        if (!res) {
          message.success(t('Email is free!'));
          return true;
        } else {
          message.error(t('Email already using!'));
          return false;
        }
      } finally {
        setIsCheckingEmail(false);
      }
    };

    const debouncedEmailValidation = useCallback(
      (email: string): Promise<boolean> => {
        return new Promise((resolve) => {
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }

          debounceTimerRef.current = setTimeout(async () => {
            const isValid = await validateEmail(email);
            resolve(isValid);
          }, 800);
        });
      },
      [applicationStore, t]
    );

    const validatePersonalEmails = (_: any, value: string) => {
      if (!value || value.trim() === '') {
        return Promise.resolve();
      }

      const emails = value.split(',').map(e => e.trim()).filter(Boolean);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      const invalidEmails = emails.filter(email => !emailRegex.test(email));
      
      if (invalidEmails.length > 0) {
        return Promise.reject(
          new Error(t('Invalid Email!') || `Invalid emails: ${invalidEmails.join(', ')}`)
        );
      }

      return Promise.resolve();
    };

    return (
      <Modal 
        title={t("InviteNewUser")} 
        open={isModalOpen} 
        footer={null} 
        onCancel={hideModal}
        confirmLoading={isSubmitting}
      >
        <Spin spinning={isSubmitting} tip={t("SendingInvitation")} size="large">
          <Form
            form={form}
            onFinish={onFinish}
            labelCol={{ span: 24 }}
            initialValues={{ chiefId: applicationStore.user.id }}
            disabled={isSubmitting}
          >
            <Form.Item name="chiefId" hidden><Input /></Form.Item>

            <Form.Item 
              label={t("Email")} 
              name="email"
              validateStatus={isCheckingEmail ? "validating" : undefined}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: t('email is required!'),
                },
                {
                  type: "email",
                  message: t('Invalid email!'),
                },
                {
                  validator: async (_, value) => {
                    if (!value || value.length <= 3) {
                      return Promise.resolve();
                    }

                    const isFree = await debouncedEmailValidation(value);
                    if (isFree) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject(new Error(t('Email already using!')));
                    }
                  },
                },
              ]}
            >
              <Input 
                placeholder="user@example.com"
                suffix={isCheckingEmail ? <LoadingOutlined spin /> : null}
              />
            </Form.Item>

            <Form.Item label={t("FirstName")} name="firstName" rules={[{ required: true }]}>
              <Input placeholder={t("FirstName")} />
            </Form.Item>

            <Form.Item label={t("LastName")} name="lastName" rules={[{ required: true }]}>
              <Input placeholder={t("LastName")} />
            </Form.Item>

            <Form.Item 
              label={t("PersonalEmailsOptional")} 
              name="personalEmails"
              rules={[{ validator: validatePersonalEmails }]}
            >
              <Input.TextArea
                placeholder={t("PersonalEmailsHint")}
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </Form.Item>

            <Form.Item
              label={t('CompanyRole')}
              name="roles"
              rules={[{ required: true, type: 'array', min: 1 }]}
            >
              <Select
                mode="multiple"
                placeholder={t('SelectRoles')}
                options={[
                  { value: 'Manager',   label: 'Manager (can manage users)' },
                  { value: 'User',      label: 'User (can manage projects)' },
                  { value: 'Financier', label: 'Financier (salary access)' },
                  { value: 'Viewer',    label: 'Viewer' },
                ]}
              />
            </Form.Item>

            <Form.Item label={t("user.JobTitle")} name="jobTitleId" rules={[{ required: true }]}>
              <Select
                placeholder={t("SelectJobTitle")}
                options={employeesStore.jobTitles.map(jt => ({ value: jt.id, label: jt.title }))}
              />
            </Form.Item>

            <Form.Item label={t("ReportingManager")} name="chief">
              <AutoComplete
                placeholder={t("ChiefUser")}
                defaultValue={applicationStore.user.email}
                onChange={(_, opt: any) => form.setFieldValue("chiefId", opt?.id)}
                options={employeesStore.users?.map(u => ({
                  value: u.email,
                  id: u.id,
                  label: (
                    <Flex gap={5}>
                      <Image
                        width={40} height={40} preview={false}
                        style={{ borderRadius: "50%", objectFit: "cover" }}
                        src={u.photoId
                          ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_100/${u.photoId}`
                          : `https://res.cloudinary.com/dnl3x07wo/image/upload/w_100/z03w7hlsy8hcoic9dlcn`}
                      />
                      <span>{u.email}</span>
                    </Flex>
                  ),
                }))}
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                style={{ float: "right", height: 48 }}
                loading={isSubmitting}
                disabled={isCheckingEmail}
              >
                {t("AddEmployee")} <i className="fa-solid fa-arrow-right-long" />
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    );
  }
);